import {
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useToast,
  HStack,
} from '@chakra-ui/react';
import { AsyncSelect } from 'chakra-react-select';
import { useNavigate } from 'react-router-dom';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { useRef, useMemo, useEffect } from 'react';
import { useDebounceCallback } from 'usehooks-ts';
import { useForm, Controller } from 'react-hook-form';
import { Logo } from '@duke-hq/ui/components/Logo/Logo';
import { LogoFooter } from '@duke-hq/ui/components/LogoFooter/LogoFooter';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';

interface SignUpFormProps {
  email: string;
  site: string | null;
}

export function AssignPrimarySiteLocation() {
  const { user } = useAuthTenant();
  const toast = useToast();
  const formRef = useRef<HTMLFormElement>(null);
  const navigate = useNavigate();

  // Early guard to ensure user has a default site location
  // If not, redirect to home page
  // This is to prevent the user from being stuck on this page without a site location
  useEffect(() => {
    if (!user?.defaultSiteLocation) {
      navigate('/');
    }
  }, [user?.defaultSiteLocation, navigate]);

  const defaultFormState: SignUpFormProps = {
    email: user?.email ?? '',
    site: null,
  };

  const {
    handleSubmit,
    register,
    control,
    clearErrors,
    formState: { errors, isValid },
  } = useForm<SignUpFormProps>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    defaultValues: {
      ...defaultFormState,
    },
  });

  const meQuery = trpcTenant.me.useQuery();

  const { data: sitesList, isPending: isSitesPending } =
    trpcTenant.auth.listSiteLocations.useQuery();

  const assignPrimarySiteLocation = trpcTenant.users.assignPrimarySiteLocation.useMutation({
    onSuccess: async () => {
      toast({
        title: t`Activation complete`,
        description: t`You have successfully completed your activation.`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });

      await meQuery.refetch();
      navigate('/');
    },
    onError: (error) => {
      toast({
        title: t`Error`,
        description: error.message || t`Failed to complete activation.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    },
  });

  const sitesOptions = useMemo(() => {
    return (
      sitesList?.map((site) => ({
        value: site.id,
        label: site.state ? `${site.name}, ${site.state}` : site.name,
      })) || []
    );
  }, [sitesList]);

  const loadSitesOptions = useDebounceCallback<
    (
      inputValue: string,
      callback: (
        opts: {
          value: string;
          label: string;
        }[],
      ) => void,
    ) => void
  >((inputValue: string, callback) => {
    if (sitesOptions.length > 0) {
      callback(
        sitesOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(inputValue.toLowerCase()) ||
            option.value.toLowerCase().includes(inputValue.toLowerCase()),
        ),
      );
    } else {
      callback([]);
    }
  }, 500);

  const onSubmit = async (data: SignUpFormProps) => {
    if (data.site != null && data.site !== '') {
      await assignPrimarySiteLocation.mutateAsync({
        email: data.email,
        siteId: data.site,
      });
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg={useColorModeValue('bg.muted', 'gray.800')}
      p="32px"
    >
      <Stack mx="auto" width="auto" maxW="530px" gap="32px" align="center">
        <Logo />

        <Stack
          align="center"
          width="full"
          px="64px"
          py="32px"
          gap="32px"
          bg="bg.surface"
          borderColor="border.emphasis"
          borderRadius="12px"
        >
          <Text
            fontSize="3xl"
            lineHeight="10"
            fontWeight="semibold"
            color="fg.default"
            align="center"
          >
            <Trans>Welcome to Duke</Trans>
          </Text>

          <Box width="100%" maxWidth="400px" textAlign="center" gap={8}>
            <Text
              fontSize="xl"
              fontWeight="semibold"
              lineHeight="7"
              color="fg.default"
              align="center"
            >
              <Trans>You have been added to {user.tenant.name}</Trans>
            </Text>

            <Text fontSize="md" fontWeight="medium" color="fg.muted" align="center">
              <Trans>Please complete your details to finalise your sign up</Trans>
            </Text>
          </Box>

          <Box
            as="form"
            width="100%"
            onSubmit={handleSubmit(onSubmit)}
            style={{ width: '100%' }}
            ref={formRef}
            noValidate
          >
            <Stack gap="16px" p={4}>
              <FormControl isInvalid={!!errors.email} isRequired>
                <FormLabel htmlFor="email">
                  <Trans>Email</Trans>
                </FormLabel>
                <Input
                  autoFocus
                  type="email"
                  autoComplete="off"
                  value={user?.email || ''}
                  {...register('email', {
                    required: t`Email is required`,
                    pattern: {
                      value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                      message: t`Please enter a valid email address`,
                    },
                    onChange: () => {
                      if (errors.email) {
                        clearErrors('email');
                      }
                    },
                  })}
                  isDisabled={!!user?.email}
                  size="md"
                  _disabled={{
                    bg: 'gray.100',
                    color: 'gray.500',
                    cursor: 'not-allowed',
                  }}
                />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>

                {user?.email ? (
                  <Box mt={2}>
                    <HStack spacing={1}>
                      <Text fontSize="sm" color="fg.muted">
                        <Trans>Email cannot be changed</Trans>
                      </Text>
                    </HStack>
                  </Box>
                ) : null}
              </FormControl>

              {isSitesPending ? (
                <Center p={2}>
                  <Spinner size="sm" />
                </Center>
              ) : sitesOptions.length === 0 ? (
                <Center p={8}>
                  <Text textAlign="center">
                    <Trans>No sites found</Trans>
                  </Text>
                </Center>
              ) : (
                <FormControl isInvalid={!!errors.site} isRequired>
                  <FormLabel htmlFor="site">
                    <Trans>Primary site</Trans>
                  </FormLabel>
                  <Controller
                    name="site"
                    control={control}
                    render={({ field }) => (
                      <AsyncSelect
                        inputId="site"
                        name="site"
                        placeholder={t`Select site`}
                        defaultOptions={sitesOptions}
                        loadOptions={loadSitesOptions}
                        selectedOptionColorScheme={'brand'}
                        onChange={(val: { value: string; label: string } | null) =>
                          field.onChange(val?.value)
                        }
                        isLoading={isSitesPending}
                        isDisabled={sitesOptions.length === 0}
                      />
                    )}
                    rules={{
                      required: t`Please select a site`,
                    }}
                  />
                  <FormErrorMessage>{errors.site?.message}</FormErrorMessage>
                </FormControl>
              )}

              <Button
                type="submit"
                isLoading={assignPrimarySiteLocation.isPending}
                loadingText="Submitting"
                isDisabled={!isValid}
              >
                <Trans>Sign in</Trans>
              </Button>
            </Stack>
          </Box>

          <Flex flexDirection="column" justify="center" align="center">
            <Text fontSize="sm" color="fg.muted" align="center">
              <Trans>By continuing, you agree to the</Trans>&nbsp;
              <Link
                href="https://drive.google.com/file/d/1uwuClmrRcpJ9lOtKf7MH4otgG-UjXMRS/view?usp=sharing"
                isExternal
              >
                <Trans>Duke Terms of Service</Trans>
              </Link>
              &nbsp;
              <Trans>and</Trans>&nbsp;
              <Link href="https://duke.co/privacy-policy" isExternal>
                <Trans>Privacy Statement</Trans>
              </Link>
            </Text>
          </Flex>
        </Stack>

        <LogoFooter />
      </Stack>
    </Flex>
  );
}
